import type { CSSProperties } from 'react';
import { useRef } from 'react';
import { create } from 'zustand';

interface PrerenderState {
  /** Prerender target by scope, one section per scope. */
  targets: Record<string, string | undefined>;
  /** Set prerender target for a scope. Pass undefined to clear. */
  setTarget: (scope: string, sectionId: string | undefined) => void;
}

const usePrerenderStore = create<PrerenderState>((set) => ({
  targets: {},
  setTarget: (scope, sectionId) =>
    set((state) => {
      if (state.targets[scope] === sectionId) return state;
      return { targets: { ...state.targets, [scope]: sectionId } };
    }),
}));

const hiddenStyle: CSSProperties = { contentVisibility: 'hidden', height: 0, overflow: 'hidden' };

/** Prerendered sections mount hidden so opening them is instant. */
export function usePrerenderSection(scope: string, sectionId: string, isOpen: boolean) {
  const isPrerendered = usePrerenderStore((state) => state.targets[scope] === sectionId);
  const shouldMount = isOpen || isPrerendered;
  const style = shouldMount && !isOpen ? hiddenStyle : undefined;

  return { shouldMount, style };
}

/** Debounces the mount by 150ms so quick scrolling does not churn the DOM. */
export function usePrerenderTrigger(scope: string) {
  const setTarget = usePrerenderStore((state) => state.setTarget);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const prerender = (sectionId: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setTarget(scope, sectionId), 150);
  };

  const clear = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setTarget(scope, undefined);
  };

  return { prerender, clear };
}
